import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import {
  fetchConfig,
  cartAddJsonBody,
  toNumber,
  applyDiscountCode,
  fetchUpdatedCartSections,
  setCartDiscountCodes,
  getOfferGiftCode,
  getSavedDiscountCodes,
  applyOfferDiscountCodes,
} from '@theme/utilities';

/**
 * @typedef {object} SubscriptionFormRefs
 * @property {HTMLElement[]} errorElements - Array of error elements.
 * @property {HTMLButtonElement[]} submitButtons - Array of submit buttons.
 * @property {HTMLInputElement} quantityInput - Quantity input element.
 * @property {HTMLInputElement[]} sellingPlanInputs - Array of selling plan input elements.
 * @property {HTMLInputElement} discountCodeInput - Discount code input element.
 * @extends Component<SubscriptionFormRefs>
 */
class SubscriptionForm extends Component {
  requiredRefs = ['errorElements', 'submitButtons', 'quantityInput', 'sellingPlanInputs', 'discountCodeInput'];

  /**
   * @type {string}
   */
  static SUBSCRIPTION_PLAN = 'subscription';

  /**
   * @type {string}
   */
  static ONETIME_PLAN = 'onetime';

  /**
   * @type {NodeListOf<HTMLInputElement>}
   */
  subscribeGiftFields;

  /**
   * @type {HTMLElement | null}
   */
  bundleSizeText;

  /**
   * @type {number}
   */
  price = 0;

  /**
   * @type {number}
   */
  servings = 0;

  /**
   * @type {number}
   */
  giftsPrice = 0;

  /**
   * @type {boolean}
   */
  isSubscriptionGift = false;

  /**
   * @type {boolean}
   */
  headerPriceIncludesGifts = false;

  /**
  * @type {HTMLElement | null}
  */
  badgeSlider = null;

  /**
   * @type {NodeListOf<HTMLElement> | null}
   */
  addToCartBtnText = null;

  connectedCallback() {
    super.connectedCallback();

    this.parent = this.closest('[data-product]');

    if (!this.parent) {
      return;
    }

    this.isSubmitProcessed = false;
    this.subscribeGiftFields = this.querySelectorAll('[data-subscribe-gift-field], [data-offer-gift-field]');
    this.bundleSizeText = this.querySelector('[data-bundle-size-target]');
    this.badgeSlider = this.parent.querySelector('.badge-slider');
    this.servings = toNumber(this.dataset.servings, 0);
    this.price = toNumber(this.dataset.price);
    this.giftsPrice = toNumber(this.dataset.giftsPrice, 0);
    this.isSubscriptionGift = this.dataset.isSubscriptionGift === 'true';
    this.headerPriceIncludesGifts = this.dataset.headerPriceIncludesGifts === 'true';
    this.addToCartBtnText = document.querySelectorAll('[data-add-to-cart-text]');
    this.priceFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    });
    this.init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  init() {
    this.updateOptionPricesWithGifts();

    const selectedInput = /** @type {HTMLInputElement} */ (this.querySelector('input[name="selling_plan"]:checked'));

    if (selectedInput) {
      const label = /** @type {HTMLElement} */ (selectedInput.closest('.sub-form-v4__option'));
      this.refs.quantityInput.value = toNumber(label.dataset.count, 1).toString();
      this.updateFirstImage(label);
      this.updateBadgeSlider(label);
      this.updateDiscountCode(label);
    }

    this.updateGiftFields(this.isSubscription);
    this.updateAddToCartText(this.isSubscription);
    this.updateBundleSizeText();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleOptionClick(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    const label = /** @type {HTMLInputElement} */ (target.closest('.sub-form-v4__option'));

    if (!label) {
      return;
    }

    const isSubscription = this.isSubscription;
    const quantity = toNumber(label.dataset.count, 1);
    this.refs.quantityInput.value = quantity.toString();
    this.updateGiftFields(isSubscription);
    this.updateFirstImage(label);
    this.updateBadgeSlider(label);
    this.updateAddToCartText(isSubscription);
    this.updateDiscountCode(label);

    if (isSubscription) {
      this.updateBundleSizeText();
    }

    const submitButton = this.refs.submitButtons.find(btn => {
      const btnPlan = btn.dataset.plan;
      return isSubscription
        ? btnPlan === SubscriptionForm.SUBSCRIPTION_PLAN
        : btnPlan === SubscriptionForm.ONETIME_PLAN;
    });

    if (submitButton) {
      const rect = submitButton.getBoundingClientRect();
      const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

      if (!isVisible) {
        this.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {SubmitEvent} event - The submit event.
   */
  async handleSubmit(event) {
    event.preventDefault();
    const currentSubmitButton = /** @type {HTMLButtonElement} */ (event.submitter);

    if (this.isSubmitProcessed) {
      return;
    }

    this.isSubmitProcessed = true;

    const errorElements = this.querySelectorAll('.sub-form-v4__error');
    errorElements.forEach((errorElement) => {
      errorElement.textContent = '';
      errorElement.classList.add('hidden');
    });

    this.refs.submitButtons.forEach((button) => {
      button.classList.add('is-loading');
    });

    try {
      const form = this.querySelector('form');

      if (!form) throw new Error('Product form element missing');

      // Offer gifts: remember the customer's codes before the cart is cleared, to set them back with the gift code
      const offerGiftCode = getOfferGiftCode(form);
      /** @type {string[] | null} */
      const savedDiscountCodes =
        offerGiftCode === null ? null : await getSavedDiscountCodes(this.dataset.clearCart === 'true');

      if (this.dataset.clearCart === 'true') {
        await this.#clearCart();
      }

      const formData = new FormData(form);

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      let cartItemComponentsSectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
        formData.append('sections', cartItemComponentsSectionIds.join(','));
      });

      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify(cartAddJsonBody(formData)) })
      );
      const responseData = await response.json();

      if (responseData.status) {
        // The cart may already be cleared, so give the customer's codes back
        if (savedDiscountCodes?.length) {
          await setCartDiscountCodes(savedDiscountCodes).catch((error) => console.error(error));
        }

        this.dispatchEvent(
          new CartAddEvent({}, this.id, {
            didError: true,
            itemCount: Number(formData.get('quantity')) || 1,
            productId: this.dataset.productId,
          })
        );

        throw new Error(responseData.message);
      }
      const id = formData.get('id');

      if (!id) throw new Error('Form ID is required');

      let sectionsToUse = responseData.sections;
      const discountCode = formData.get('discount');

      if (savedDiscountCodes && offerGiftCode !== null) {
        const updatedCart = await applyOfferDiscountCodes({
          form,
          giftCode: offerGiftCode,
          formCode: typeof discountCode === 'string' ? discountCode : null,
          savedCodes: savedDiscountCodes,
          sections: Object.keys(responseData.sections || {}),
          sectionsUrl: window.location.pathname,
        });
        sectionsToUse = updatedCart.sections || sectionsToUse;
      } else if (discountCode && typeof discountCode === 'string') {
        await applyDiscountCode(discountCode);
        sectionsToUse = await fetchUpdatedCartSections(responseData.sections);
      }

      const returnToField = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="return_to"]'));
      const willRedirect = !!(returnToField && returnToField.value);

      this.dispatchEvent(
        new CartAddEvent({}, id.toString(), {
          itemCount: Number(formData.get('quantity')) || 1,
          productId: this.dataset.productId,
          sections: sectionsToUse,
          willRedirect,
        })
      );

      if (willRedirect) {
        window.location.href = returnToField.value;
      }
    } catch (error) {
      console.error('Form submission failed:', error);

      const parentErrorElement = currentSubmitButton.closest('.sub-form-v4__controls')?.querySelector('.sub-form-v4__error');
      if (parentErrorElement) {
        parentErrorElement.textContent = error.message || 'Failed to submit form';
        parentErrorElement.classList.remove('hidden');
      }
    } finally {
      this.refs.submitButtons.forEach((button) => {
        button.disabled = false;
        button.classList.remove('is-loading');
      });

      // Reset flag after a delay to allow re-submission
      setTimeout(() => {
        this.isSubmitProcessed = false;
      }, 1000);
    }
  }

  /**
   * Clears the cart before adding new items.
   */
  async #clearCart() {
    const fetchCfg = fetchConfig('javascript');
    await fetch('/cart/clear.js', {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    });

    document.dispatchEvent(
      new CustomEvent(ThemeEvents.cartClear, {
        bubbles: true,
      })
    );
  }

  get сurrentPlan() {
    const checkedInput = this.refs.sellingPlanInputs ? this.refs.sellingPlanInputs.find(input => input.checked) : null;

    return checkedInput?.value === ''
      ? SubscriptionForm.ONETIME_PLAN
      : SubscriptionForm.SUBSCRIPTION_PLAN;
  }

  /**
   * @returns {boolean}
   */
  get isSubscription() {
    return this.сurrentPlan === SubscriptionForm.SUBSCRIPTION_PLAN;
  }

  /**
   * Update discount code input value based on selected plan
   * @param {HTMLElement} option - Selected option element
   */
  updateDiscountCode(option) {
    const discountCodeInput = this.refs.discountCodeInput;

    if (discountCodeInput) {
      discountCodeInput.value = option.dataset.discountCode || '';
    }
  }

  /**
   * Update gift fields based on selected plan
   * @param {boolean} isSubscription - Whether subscription plan is selected
   */
  updateGiftFields(isSubscription) {
    this.subscribeGiftFields.forEach(field => {
      if (isSubscription) {
        // Enable field for subscribe plan
        field.removeAttribute('disabled');
      } else {
        // Disable field for onetime plan
        field.setAttribute('disabled', 'disabled');
      }
    });
  }

  /**
   * Update bundle size text based on current quantity
   */
  updateBundleSizeText() {
    if (!this.bundleSizeText) {
      return;
    }

    const quantity = toNumber(this.refs.quantityInput.value, 1);
    const bundleSize = quantity * this.servings;
    const bagText = quantity === 1 ? 'bag' : 'bags';
    this.bundleSizeText.textContent = `${quantity} ${bagText} (${bundleSize} servings)`;
  }

  /**
   * Update first image in media gallery
   * @param {HTMLElement} option - Selected option element
   */
  updateFirstImage(option) {
    const firstImageUrl = option.dataset.firstImage;
    if (!firstImageUrl || !this.parent) return;

    const firstSlide = this.parent.querySelector('[data-first-slide-image]');
    if (!firstSlide) return;

    const img = firstSlide.querySelector('img');
    if (!img) return;

    img.src = firstImageUrl;
    img.srcset = firstImageUrl;
    img.setAttribute('data_max_resolution', firstImageUrl);

    const thumbnail = this.parent.querySelector('[data-first-thumbnail-image]');
    if (thumbnail) {
      const thumbnailImg = thumbnail.querySelector('img');
      if (thumbnailImg) {
        thumbnailImg.src = firstImageUrl;
        thumbnailImg.srcset = firstImageUrl;
      }
    }
  }

  /**
   * Add gifts price to the old price of each subscription option and show the total discount in its badge
   */
  updateOptionPricesWithGifts() {
    if (!this.headerPriceIncludesGifts || this.giftsPrice <= 0 || !this.price) {
      return;
    }

    /** @type {NodeListOf<HTMLElement>} */
    const options = this.querySelectorAll('.sub-form-v4__option:not(.sub-form-v4__option--onetime)');

    options.forEach(option => {
      const count = toNumber(option.dataset.count, 1);
      const discountPercentage = toNumber(option.dataset.discountPercentage, 0);
      // Same rounding as the Liquid `times` / `divided_by` filters in subscription-option-v4
      const discountedPrice = Math.floor(this.price * (100 - discountPercentage) / 100) * count;
      const oldPrice = this.price * count + this.giftsPrice;
      const currentPrice = this.isSubscriptionGift ? discountedPrice : discountedPrice + this.giftsPrice;
      const totalDiscount = Math.round((oldPrice - currentPrice) / oldPrice * 100);

      const prices = option.querySelector('.sub-form-v4__option-prices');
      const currentEl = prices?.querySelector('.sub-form-v4__option-price:not(.sub-form-v4__option-price--old)');
      let oldEl = prices?.querySelector('.sub-form-v4__option-price--old');

      if (prices && !oldEl) {
        oldEl = document.createElement('div');
        oldEl.className = 'sub-form-v4__option-price sub-form-v4__option-price--old';
        prices.append(oldEl);
      }

      if (currentEl) {
        currentEl.textContent = this.formatPrice(currentPrice);
      }

      if (oldEl) {
        oldEl.textContent = this.formatPrice(oldPrice);
      }

      const badgeText = option.querySelector('.sub-form-v4__option-badge-text');

      if (badgeText) {
        badgeText.textContent = `${totalDiscount}% OFF`;
      }
    });
  }

  /**
   * Update badge slider with new price and per-serving price
   * @param {HTMLElement} option - Selected option element
   */
  updateBadgeSlider(option) {
    if (!this.badgeSlider) {
      return;
    }

    const discountPercentage = toNumber(option.dataset.discountPercentage, 0);
    const discountedPrice = this.price * (1 - discountPercentage / 100);
    const priceBlock = this.badgeSlider.querySelector('.badge-slider__price');
    const perServingBlock = this.badgeSlider.querySelector('[data-badge-slider-per-serving]');

    if (priceBlock) {
      priceBlock.textContent = this.formatPrice(discountedPrice);
    }

    if (perServingBlock) {
      perServingBlock.textContent = this.formatPrice(discountedPrice / this.servings);
    }
  }

  /**
     * Update add to cart text based on selected plan
     * @param {boolean} isSubscription - Whether subscription plan is selected
     */
  updateAddToCartText(isSubscription) {
    if (!this.addToCartBtnText) {
      return;
    }

    this.addToCartBtnText.forEach(btn => {
      btn.textContent = isSubscription ? 'START NOW' : 'TRY NOW';
    });
  }

  /**
   * @param {number} cents - The price in cents
   */
  formatPrice(cents) {
    return this.priceFormatter.format(cents / 100);
  }
}

if (!customElements.get('subscription-form-v4')) {
  customElements.define('subscription-form-v4', SubscriptionForm);
}
